import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import { api } from '../services/api';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function DealerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dealer, setDealer] = useState<any>(null);
  const [team, setTeam] = useState<{ contacts: any[]; salesReps: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editField, setEditField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [editingShipping, setEditingShipping] = useState(false);
  const [shippingForm, setShippingForm] = useState({ street: '', city: '', state: '', zip: '', country: '' });
  // Additional (non-login) contacts — see the Additional Contacts card below.
  const [addlContacts, setAddlContacts] = useState<any[]>([]);
  const [addingContact, setAddingContact] = useState(false);
  const [addlSaving, setAddlSaving] = useState(false);
  const [addlError, setAddlError] = useState('');
  const emptyContact = { name: '', title: '', email: '', phone: '', phoneAlt: '', notes: '' };
  const [newContact, setNewContact] = useState(emptyContact);

  useEffect(() => {
    if (id) loadDealer();
  }, [id]);

  const loadDealer = async () => {
    try {
      setLoading(true);
      const [dealerData, teamData] = await Promise.all([
        api.dealers.getDealer(id!),
        api.dealers.getDealerTeam(id!),
      ]);
      setDealer(dealerData);
      setTeam(teamData);
      await loadAddlContacts();
    } catch (err) {
      console.error('Failed to load dealer:', err);
    } finally {
      setLoading(false);
    }
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}`,
    'Content-Type': 'application/json',
  });

  const loadAddlContacts = async () => {
    try {
      const res = await fetch(`${API_BASE}/dealers/${id}/additional-contacts`, { headers: authHeaders() });
      if (res.ok) setAddlContacts(await res.json());
    } catch (err) {
      console.error('Failed to load additional contacts:', err);
    }
  };

  const saveAddlContact = async () => {
    if (!newContact.name.trim()) { setAddlError('Name is required'); return; }
    setAddlSaving(true);
    setAddlError('');
    try {
      const res = await fetch(`${API_BASE}/dealers/${id}/additional-contacts`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(newContact),
      });
      if (!res.ok) throw new Error((await res.json())?.message || 'Failed to save');
      setNewContact(emptyContact);
      setAddingContact(false);
      await loadAddlContacts();
    } catch (e: any) {
      setAddlError(e?.message || 'Failed to save contact');
    } finally {
      setAddlSaving(false);
    }
  };

  const deleteAddlContact = async (contactId: string) => {
    if (!window.confirm('Remove this contact?')) return;
    try {
      await fetch(`${API_BASE}/dealers/additional-contacts/${contactId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      await loadAddlContacts();
    } catch (err) {
      console.error('Failed to remove contact:', err);
    }
  };

  const toggleSmsOptIn = async () => {
    try {
      const next = !dealer?.smsOptIn;
      await api.dealers.updateDealer(id!, { smsOptIn: next } as any);
      setDealer((prev: any) => ({ ...prev, smsOptIn: next }));
      setSuccessMsg('Saved');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err: any) {
      alert(err.message || 'Failed to update');
    }
  };

  const startEdit = (field: string, currentValue: string) => {
    setEditField(field);
    setEditValue(currentValue || '');
  };

  const cancelEdit = () => {
    setEditField(null);
    setEditValue('');
  };

  const saveField = async (field: string) => {
    try {
      setSaving(true);
      await api.dealers.updateDealer(id!, { [field]: editValue });
      setDealer((prev: any) => ({ ...prev, [field]: editValue }));
      setEditField(null);
      setSuccessMsg('Saved');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err: any) {
      alert(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openShippingEditor = () => {
    const s = dealer?.shippingAddress;
    setShippingForm({
      street: s?.street || '', city: s?.city || '', state: s?.state || '',
      zip: s?.zip || '', country: s?.country || '',
    });
    setEditingShipping(true);
  };
  const saveShipping = async (clear = false) => {
    try {
      setSaving(true);
      const payload = clear
        ? { shipping_address_street: null, shipping_address_city: null,
            shipping_address_state: null, shipping_address_zip: null,
            shipping_address_country: null }
        : { shipping_address_street: shippingForm.street || null,
            shipping_address_city: shippingForm.city || null,
            shipping_address_state: shippingForm.state || null,
            shipping_address_zip: shippingForm.zip || null,
            shipping_address_country: shippingForm.country || null };
      await api.dealers.updateDealer(id!, payload as any);
      setDealer((prev: any) => ({
        ...prev,
        shippingAddress: clear ? null : {
          street: shippingForm.street || null,
          city: shippingForm.city || null,
          state: shippingForm.state || null,
          zip: shippingForm.zip || null,
          country: shippingForm.country || null,
        },
      }));
      setEditingShipping(false);
      setSuccessMsg('Saved');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err: any) {
      alert(err.message || 'Failed to save shipping address');
    } finally {
      setSaving(false);
    }
  };

  const impersonateDealer = async () => {
    try {
      const result = await api.dealers.impersonateDealer(id!);
      const dealerUrl = window.location.hostname.includes('kederscreens.com')
        ? 'https://dealerportal.kederscreens.com'
        : `${window.location.protocol}//${window.location.hostname}:3001`;
      window.open(`${dealerUrl}/impersonate?token=${result.token}`, '_blank');
    } catch (err: any) {
      alert(err.message || 'Failed to impersonate');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!dealer) return <div className="p-8 text-center text-gray-500">Dealer not found</div>;

  const name = dealer.companyName || dealer.name || 'Unknown';
  const stats = dealer.stats || {};
  const rewards = dealer.rewards || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => navigate('/dealers')}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
            <StatusBadge status={dealer.status || 'active'} variant="dealer" />
            {rewards.tierIcon && (
              <span className="text-lg" title={rewards.tierDisplayName || rewards.tier?.displayName}>
                {rewards.tierIcon || rewards.tier?.icon}
              </span>
            )}
          </div>
          {dealer.legalName && (
            <p className="text-sm text-gray-500 mt-0.5">
              Legal: {dealer.legalName}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {successMsg && (
            <span className="text-sm text-green-600 font-medium">{successMsg}</span>
          )}
          <button
            onClick={impersonateDealer}
            className="px-3 py-1.5 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100"
          >
            Login As Dealer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — Company Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Information */}
          <Card title="Company Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoRow
                label="DBA / Trade Name"
                value={name}
                field="companyName"
                editField={editField}
                editValue={editValue}
                saving={saving}
                onEdit={startEdit}
                onSave={saveField}
                onCancel={cancelEdit}
                onChange={setEditValue}
              />
              <InfoRow
                label="Legal Name"
                value={dealer.legalName}
                field="legalName"
                editField={editField}
                editValue={editValue}
                saving={saving}
                onEdit={startEdit}
                onSave={saveField}
                onCancel={cancelEdit}
                onChange={setEditValue}
                placeholder="e.g. Walker Designs, Inc."
              />
              <InfoRow
                label="Email"
                value={dealer.email}
                field="email"
                editField={editField}
                editValue={editValue}
                saving={saving}
                onEdit={startEdit}
                onSave={saveField}
                onCancel={cancelEdit}
                onChange={setEditValue}
              />
              <InfoRow
                label="Phone"
                value={dealer.phone}
                field="phone"
                editField={editField}
                editValue={editValue}
                saving={saving}
                onEdit={startEdit}
                onSave={saveField}
                onCancel={cancelEdit}
                onChange={setEditValue}
              />
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Website</p>
                <p className="text-sm text-gray-900">
                  {dealer.website ? (
                    <a href={dealer.website.startsWith('http') ? dealer.website : `https://${dealer.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {dealer.website}
                    </a>
                  ) : (
                    <span className="text-gray-400 italic">Not set</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  {dealer.shippingAddress ? 'Billing Address' : 'Address'}
                </p>
                <p className="text-sm text-gray-900">
                  {dealer.street || dealer.address?.street ? (
                    <>
                      {dealer.street || dealer.address?.street}<br />
                      {[dealer.city || dealer.address?.city, dealer.state || dealer.address?.state].filter(Boolean).join(', ')}
                      {(dealer.zip || dealer.address?.zip) ? ` ${dealer.zip || dealer.address?.zip}` : ''}
                    </>
                  ) : (
                    <span className="text-gray-400 italic">No address on file</span>
                  )}
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide flex items-center gap-1">
                    Shipping Address
                    {dealer.shippingAddress && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">Different</span>
                    )}
                  </p>
                  <button
                    onClick={openShippingEditor}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {dealer.shippingAddress ? 'Edit' : '+ Add'}
                  </button>
                </div>
                {dealer.shippingAddress ? (
                  <p className="text-sm text-gray-900">
                    {dealer.shippingAddress.street}<br />
                    {[dealer.shippingAddress.city, dealer.shippingAddress.state].filter(Boolean).join(', ')}
                    {dealer.shippingAddress.zip ? ` ${dealer.shippingAddress.zip}` : ''}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Same as billing</p>
                )}
              </div>
            </div>
          </Card>

          {/* Contacts */}
          <Card title="Contacts">
            {team && (team.contacts?.length > 0 || team.salesReps?.length > 0) ? (
              <div className="space-y-3">
                {team.contacts?.map((c: any) => (
                  <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                        {(c.first_name || c.name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.name || 'Unknown'}
                          {c.is_primary && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">PRIMARY</span>}
                          {c.is_admin && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">ADMIN</span>}
                        </p>
                        <p className="text-xs text-gray-500">{c.email}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      {c.phone && <p>{c.phone}</p>}
                      {c.title && <p>{c.title}</p>}
                    </div>
                  </div>
                ))}
                {team.salesReps?.length > 0 && (
                  <>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-4 mb-2">Sales Reps</p>
                    {team.salesReps.map((r: any) => (
                      <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
                            {(r.first_name || r.name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : r.name || 'Unknown'}
                              <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                {(r.role || 'rep').toUpperCase()}
                              </span>
                            </p>
                            <p className="text-xs text-gray-500">{r.email}</p>
                          </div>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          {r.phone && <p>{r.phone}</p>}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No contacts on file</p>
            )}
          </Card>

          {/* Additional contacts — address book only, NO portal access.
              dealer_contacts above is the login table (email NOT NULL, every row a
              login), so an AP clerk or a second phone number for the owner has
              no business being in it. These are plain records. */}
          <Card title="Additional Contacts">
            <p className="text-xs text-gray-500 mb-3">
              People who don't log in — accounts payable, warehouse, installers, extra phone numbers.
            </p>

            {addlContacts.length > 0 ? (
              <div className="space-y-2 mb-3">
                {addlContacts.map((c: any) => (
                  <div key={c.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {c.name}
                        {c.title && <span className="ml-2 text-xs font-normal text-gray-500">{c.title}</span>}
                      </p>
                      {c.email && <p className="text-xs text-gray-500">{c.email}</p>}
                      {c.notes && <p className="text-xs text-gray-400 mt-1 italic">{c.notes}</p>}
                    </div>
                    <div className="text-right text-xs text-gray-600 shrink-0 ml-3">
                      {c.phone && <p>{c.phone}</p>}
                      {c.phone_alt && <p className="text-gray-400">{c.phone_alt}</p>}
                      <button
                        onClick={() => deleteAddlContact(c.id)}
                        className="text-[11px] text-red-600 hover:underline mt-1"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic mb-3">None added</p>
            )}

            {addingContact ? (
              <div className="space-y-2 border-t pt-3">
                <div className="grid grid-cols-2 gap-2">
                  <input placeholder="Name *" value={newContact.name}
                    onChange={e => setNewContact({ ...newContact, name: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Title / role" value={newContact.title}
                    onChange={e => setNewContact({ ...newContact, title: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Email" value={newContact.email}
                    onChange={e => setNewContact({ ...newContact, email: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Phone" value={newContact.phone}
                    onChange={e => setNewContact({ ...newContact, phone: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Additional phone" value={newContact.phoneAlt}
                    onChange={e => setNewContact({ ...newContact, phoneAlt: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Notes" value={newContact.notes}
                    onChange={e => setNewContact({ ...newContact, notes: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </div>
                {addlError && <p className="text-xs text-red-600">{addlError}</p>}
                <div className="flex gap-2">
                  <button onClick={saveAddlContact} disabled={addlSaving}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {addlSaving ? 'Saving...' : 'Save Contact'}
                  </button>
                  <button onClick={() => { setAddingContact(false); setAddlError(''); }}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingContact(true)}
                className="text-sm text-blue-600 hover:underline">+ Add contact</button>
            )}
          </Card>

          {/* Notes */}
          <Card title="Notes">
            {editField === 'notes' ? (
              <div>
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => saveField('notes')}
                    disabled={saving}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={cancelEdit} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => startEdit('notes', dealer.notes || '')}
                className="text-sm text-gray-700 whitespace-pre-wrap min-h-[60px] cursor-pointer hover:bg-gray-50 rounded p-2 -m-2"
              >
                {dealer.notes || <span className="text-gray-400 italic">Click to add notes...</span>}
              </div>
            )}
          </Card>

          <Card title="Communications">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!dealer.smsOptIn}
                onChange={toggleSmsOptIn}
              />
              <span>SMS order notifications</span>
            </label>
            <p className="text-xs text-gray-400 mt-1">
              Dealer has consented to transactional order texts (ready / balance / shipped / delivered).
            </p>
          </Card>

          <DealerCallHistory dealerId={id!} />
        </div>

        {/* Right column — Account & Stats */}
        <div className="space-y-6">
          {/* Account */}
          <Card title="Account">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Account Type</span>
                <select
                  value={dealer.accountType || dealer.account_type || 'house'}
                  onChange={async (e) => {
                    try {
                      setSaving(true);
                      await api.dealers.updateDealer(id!, { account_type: e.target.value });
                      setDealer((prev: any) => ({ ...prev, accountType: e.target.value, account_type: e.target.value }));
                      setSuccessMsg('Saved');
                      setTimeout(() => setSuccessMsg(''), 2000);
                    } catch (err: any) {
                      alert(err.message || 'Failed to save');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="text-sm font-semibold text-gray-900 border border-gray-200 rounded px-2 py-1 bg-white cursor-pointer"
                  disabled={(dealer.accountType || dealer.account_type) === 'rep'}
                >
                  <option value="house">House Account</option>
                  <option value="direct">Direct (Sales Rep)</option>
                  {(dealer.accountType || dealer.account_type) === 'rep' && (
                    <option value="rep">Rep Firm — use Dealers page to change</option>
                  )}
                </select>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Lead Source</span>
                <select
                  value={dealer.leadSource || dealer.lead_source || ''}
                  onChange={async (e) => {
                    try {
                      setSaving(true);
                      await api.dealers.updateDealer(id!, { lead_source: e.target.value });
                      setDealer((prev: any) => ({ ...prev, leadSource: e.target.value, lead_source: e.target.value }));
                      setSuccessMsg('Saved');
                      setTimeout(() => setSuccessMsg(''), 2000);
                    } catch (err: any) {
                      alert(err.message || 'Failed to save');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="text-sm font-semibold text-gray-900 border border-gray-200 rounded px-2 py-1 bg-white cursor-pointer"
                >
                  <option value="">Not set</option>
                  <option value="jotform_organic">JotForm (Direct)</option>
                  <option value="website">Website</option>
                  <option value="marketing">Marketing / Ads</option>
                  <option value="trade_show">Trade Show</option>
                  <option value="sales_rep_referral">Sales Rep Referral</option>
                  <option value="rep_firm_referral">Rep Firm Referral</option>
                  <option value="existing_dealer">Dealer Referral</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Discount</span>
                <span className="text-sm font-semibold text-gray-900">{dealer.discountPercent || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500" title="Per-dealer commission rate paid to the assigned internal sales rep. Leave blank to use default tier rule (≤50% discount → 10%, >50% → 5%).">
                  Sales Rep Commission %
                </span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  defaultValue={dealer.internalCommissionRateOverride ?? ''}
                  placeholder="default"
                  onBlur={async (e) => {
                    const raw = e.currentTarget.value.trim();
                    const next = raw === '' ? null : Number(raw);
                    if (raw !== '' && (Number.isNaN(next as number) || (next as number) < 0 || (next as number) > 100)) {
                      alert('Enter a number between 0 and 100, or leave blank for default.');
                      e.currentTarget.value = String(dealer.internalCommissionRateOverride ?? '');
                      return;
                    }
                    if ((dealer.internalCommissionRateOverride ?? null) === next) return;
                    try {
                      await api.dealers.updateDealer(id!, { internalCommissionRateOverride: next } as any);
                      setDealer((prev: any) => ({ ...prev, internalCommissionRateOverride: next }));
                    } catch (err: any) { alert(err.message); }
                  }}
                  className="w-20 text-right text-sm font-semibold text-gray-900 border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500" title="When an order has at least this many screens, freight switches from a live carrier quote to a per-screen flat rate (set below). Blank = feature off.">
                  Flat-Rate Threshold (screens)
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  defaultValue={dealer.flatRateThresholdScreens ?? ''}
                  placeholder="off"
                  onBlur={async (e) => {
                    const raw = e.currentTarget.value.trim();
                    const next = raw === '' ? null : parseInt(raw, 10);
                    if (raw !== '' && (Number.isNaN(next as number) || (next as number) < 1)) {
                      alert('Enter a whole number ≥ 1, or leave blank.');
                      e.currentTarget.value = String(dealer.flatRateThresholdScreens ?? '');
                      return;
                    }
                    if ((dealer.flatRateThresholdScreens ?? null) === next) return;
                    try {
                      await api.dealers.updateDealer(id!, { flatRateThresholdScreens: next } as any);
                      setDealer((prev: any) => ({ ...prev, flatRateThresholdScreens: next }));
                    } catch (err: any) { alert(err.message); }
                  }}
                  className="w-20 text-right text-sm font-semibold text-gray-900 border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-sm ${dealer.flatRateThresholdScreens ? 'text-gray-500' : 'text-gray-400'}`} title="Per-screen freight rate once threshold is met. Total freight = rate × screen count.">
                  Flat-Rate Per Screen
                </span>
                <div className="flex items-center gap-1">
                  <span className={`text-sm ${dealer.flatRateThresholdScreens ? 'text-gray-500' : 'text-gray-400'}`}>$</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    defaultValue={dealer.flatRateShippingPerScreen ?? ''}
                    placeholder="0.00"
                    disabled={!dealer.flatRateThresholdScreens}
                    onBlur={async (e) => {
                      const raw = e.currentTarget.value.trim();
                      const next = raw === '' ? null : Number(raw);
                      if (raw !== '' && (Number.isNaN(next as number) || (next as number) < 0)) {
                        alert('Enter a non-negative number, or leave blank.');
                        e.currentTarget.value = String(dealer.flatRateShippingPerScreen ?? '');
                        return;
                      }
                      if ((dealer.flatRateShippingPerScreen ?? null) === next) return;
                      try {
                        await api.dealers.updateDealer(id!, { flatRateShippingPerScreen: next } as any);
                        setDealer((prev: any) => ({ ...prev, flatRateShippingPerScreen: next }));
                      } catch (err: any) { alert(err.message); }
                    }}
                    className="w-24 text-right text-sm font-semibold text-gray-900 border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              </div>
              {dealer.flatRateThresholdScreens && dealer.flatRateShippingPerScreen ? (
                <div className="text-[11px] text-gray-500 italic -mt-2">
                  Orders with {dealer.flatRateThresholdScreens}+ screens → ${Number(dealer.flatRateShippingPerScreen).toFixed(2)}/screen flat. Smaller orders use live carrier quotes.
                </div>
              ) : null}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500" title="When a payment lands on this dealer's order, auto-attach it to an open consolidated shipment (or group it with other paid orphans). Saves admin from manually grouping them.">
                  Auto-Consolidate Paid Orders
                </span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dealer.autoConsolidateOrders || false}
                    onChange={async (e) => {
                      try {
                        await api.dealers.updateDealer(id!, { autoConsolidateOrders: e.target.checked } as any);
                        setDealer((prev: any) => ({ ...prev, autoConsolidateOrders: e.target.checked }));
                      } catch (err: any) { alert(err.message); }
                    }}
                    className="rounded text-purple-600"
                  />
                  <span className="text-sm font-semibold text-gray-900">{dealer.autoConsolidateOrders ? 'On' : 'Off'}</span>
                </label>
              </div>
              {dealer.autoConsolidateOrders && (
                <div className="text-[11px] text-gray-500 italic -mt-2">
                  When a deposit or full payment lands on an order, it auto-joins this dealer's open shipment (or groups with any other paid orphan orders).
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-700 font-medium" title="When ON, the dealer can only pay existing invoices. They cannot create orders, drafts, parts checkouts, or quotes. A popup explains why on every blocked attempt.">
                  Block New Orders
                </span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dealer.newOrdersBlocked || false}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      // Optimistic update so UI flips instantly + dependent UI
                      // (BLOCKED label, reason textarea) renders without waiting
                      // for the network round-trip. Revert on failure.
                      const prev = dealer.newOrdersBlocked;
                      setDealer((p: any) => ({ ...p, newOrdersBlocked: next }));
                      try {
                        await api.dealers.updateDealer(id!, { newOrdersBlocked: next } as any);
                      } catch (err: any) {
                        setDealer((p: any) => ({ ...p, newOrdersBlocked: prev }));
                        alert(err.message);
                      }
                    }}
                    className="rounded text-red-600"
                  />
                  <span className={`text-sm font-semibold ${dealer.newOrdersBlocked ? 'text-red-700' : 'text-gray-900'}`}>
                    {dealer.newOrdersBlocked ? 'BLOCKED' : 'OK'}
                  </span>
                </label>
              </div>
              {dealer.newOrdersBlocked && (
                <div className="space-y-1">
                  <label className="block text-xs text-red-700 font-medium" title="Shown to the dealer in the popup. Be specific: which invoices, how to pay.">
                    Reason (shown to dealer)
                  </label>
                  <textarea
                    rows={2}
                    defaultValue={dealer.newOrdersBlockReason ?? ''}
                    placeholder="e.g. Invoice INV-1234 ($5,200) is 45 days past due. Please remit payment before placing additional orders."
                    onBlur={async (e) => {
                      const next = e.currentTarget.value.trim() || null;
                      if ((dealer.newOrdersBlockReason ?? null) === next) return;
                      try {
                        await api.dealers.updateDealer(id!, { newOrdersBlockReason: next } as any);
                        setDealer((prev: any) => ({ ...prev, newOrdersBlockReason: next }));
                      } catch (err: any) { alert(err.message); }
                    }}
                    className="w-full text-xs border border-red-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Status</span>
                <StatusBadge status={dealer.status || 'active'} variant="dealer" />
              </div>
              {dealer.brandedEmail && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Branded Email</span>
                  <span className="text-sm text-gray-900">{dealer.brandedEmail}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Has Showroom</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dealer.hasShowroom || dealer.has_showroom || false}
                    onChange={async (e) => {
                      try {
                        await api.dealers.updateDealer(id!, { has_showroom: e.target.checked });
                        setDealer((prev: any) => ({ ...prev, hasShowroom: e.target.checked, has_showroom: e.target.checked }));
                      } catch (err: any) { alert(err.message); }
                    }}
                    className="rounded text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-semibold text-gray-900">{(dealer.hasShowroom || dealer.has_showroom) ? 'Yes' : 'No'}</span>
                </label>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Created</span>
                <span className="text-sm text-gray-900">
                  {dealer.createdAt ? new Date(dealer.createdAt).toLocaleDateString() : '-'}
                </span>
              </div>
            </div>
          </Card>

          {/* Order Stats */}
          <Card title="Orders">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Total Orders</span>
                <span className="text-sm font-semibold text-gray-900">{stats.total_orders || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Active (In Production)</span>
                <span className="text-sm font-semibold text-yellow-600">{stats.active_orders || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Total Spent</span>
                <span className="text-sm font-semibold text-green-700">
                  ${parseFloat(stats.total_spent || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
              <Link
                to={`/orders?dealer=${id}`}
                className="block text-center mt-3 text-sm text-blue-600 hover:text-blue-800"
              >
                View All Orders
              </Link>
            </div>
          </Card>

          {/* Rewards */}
          {rewards && (rewards.tier || rewards.tierName) && (
            <Card title="Rewards">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Tier</span>
                  <span className="text-sm font-semibold">
                    {rewards.tier?.icon || rewards.tierIcon || ''}{' '}
                    {rewards.tier?.displayName || rewards.tierDisplayName || 'Gold'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Current Points</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {(rewards.currentPoints || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Lifetime Points</span>
                  <span className="text-sm text-gray-900">
                    {(rewards.lifetimePoints || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">YTD Earned</span>
                  <span className="text-sm text-gray-900">
                    {(rewards.ytdPointsEarned || 0).toLocaleString()}
                  </span>
                </div>
                {rewards.progress?.tierProgress !== undefined && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Tier Progress</span>
                      <span>{Math.round(rewards.progress.tierProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${Math.min(rewards.progress.tierProgress, 100)}%` }}
                      />
                    </div>
                    {rewards.nextTier && (
                      <p className="text-xs text-gray-500 mt-1">
                        {rewards.progress.pointsToNextTier?.toLocaleString()} pts to {rewards.nextTier.displayName}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Credit Account */}
          {dealer.credit_limit > 0 && (
            <Card title="Credit Account">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Credit Limit</span>
                  <span className="text-sm font-semibold text-gray-900">
                    ${(dealer.credit_limit || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Balance Used</span>
                  <span className={`text-sm font-semibold ${
                    dealer.credit_balance >= dealer.credit_limit ? 'text-red-600' :
                    dealer.credit_balance > dealer.credit_limit * 0.8 ? 'text-yellow-600' :
                    'text-gray-900'
                  }`}>
                    ${(dealer.credit_balance || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Net Terms</span>
                  <span className="text-sm text-gray-900">{dealer.net_terms_days || 30} days</span>
                </div>
              </div>
            </Card>
          )}

          {/* Tax exempt status */}
          <DealerTaxStatus dealerId={id!} />
        </div>
      </div>

      {/* Shipping address editor modal */}
      {editingShipping && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Address</h3>
            <p className="text-sm text-gray-500 mb-4">
              Set a separate shipping address when it differs from billing. Clear
              to fall back to the billing address.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Street</label>
                <input value={shippingForm.street} onChange={e => setShippingForm({...shippingForm, street: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="123 Main St, Suite 100" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">City</label>
                  <input value={shippingForm.city} onChange={e => setShippingForm({...shippingForm, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">State</label>
                  <input value={shippingForm.state} onChange={e => setShippingForm({...shippingForm, state: e.target.value.toUpperCase().slice(0,2)})}
                    maxLength={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm uppercase" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">ZIP</label>
                  <input value={shippingForm.zip} onChange={e => setShippingForm({...shippingForm, zip: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Country</label>
                  <input value={shippingForm.country} onChange={e => setShippingForm({...shippingForm, country: e.target.value})}
                    placeholder="United States" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between gap-2">
              {dealer.shippingAddress && (
                <button onClick={() => saveShipping(true)} disabled={saving}
                  className="text-sm text-red-600 hover:text-red-800 font-medium">
                  Clear (use billing)
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button onClick={() => setEditingShipping(false)} disabled={saving}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button onClick={() => saveShipping(false)} disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface CallLog {
  id: string;
  direction?: string;
  counterparty_number?: string;
  handled_by_name?: string;
  duration_seconds?: number;
  call_time?: string;
  recording_url?: string;
  transcript?: string;
  summary?: string;
}

function DealerCallHistory({ dealerId }: { dealerId: string }) {
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<CallLog[]>(`/ringcentral/call-logs?dealerId=${dealerId}`);
        setCalls(Array.isArray(data) ? data : []);
      } catch {
        setCalls([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [dealerId]);

  const fmtDur = (s?: number) => (s ? `${Math.floor(s / 60)}m ${s % 60}s` : '—');
  const fmtDate = (d?: string) => (d ? new Date(d).toLocaleString() : '');

  if (loading) return <Card title="Call History"><LoadingSpinner /></Card>;

  return (
    <Card title="Call History">
      {calls.length === 0 ? (
        <p className="text-sm text-gray-400 italic py-2">No calls logged for this dealer yet.</p>
      ) : (
        <div className="space-y-3">
          {calls.map((c) => (
            <div key={c.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      c.direction === 'inbound'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {c.direction === 'inbound' ? '↓ Inbound' : '↑ Outbound'}
                  </span>
                  <span className="text-gray-500">{fmtDate(c.call_time)}</span>
                </div>
                <span className="text-gray-500 text-xs">
                  {c.handled_by_name ? `${c.handled_by_name} · ` : ''}
                  {fmtDur(c.duration_seconds)}
                </span>
              </div>
              {c.summary ? (
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{c.summary}</p>
              ) : (
                <p className="text-xs text-gray-400 italic mt-2">
                  {c.recording_url ? 'Recording available — summary pending.' : 'No summary.'}
                </p>
              )}
              {c.transcript && (
                <button
                  onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}
                  className="text-xs text-blue-600 hover:underline mt-2"
                >
                  {open[c.id] ? 'Hide transcript' : 'Show transcript'}
                </button>
              )}
              {open[c.id] && c.transcript && (
                <pre className="text-xs text-gray-600 mt-2 whitespace-pre-wrap bg-gray-50 rounded p-2 max-h-64 overflow-y-auto">
                  {c.transcript}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DealerTaxStatus({ dealerId }: { dealerId: string }) {
  const [status, setStatus] = useState<{ tax_exempt: boolean; resale_cert_url: string | null; resale_cert_notes: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.tax.getDealerStatus(dealerId);
      setStatus(data);
      setNotesDraft(data.resale_cert_notes || '');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dealerId]);

  const toggleExempt = async () => {
    if (!status) return;
    setSaving(true);
    try {
      const updated = await api.tax.setDealerStatus(dealerId, { tax_exempt: !status.tax_exempt });
      setStatus(updated);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const updated = await api.tax.setDealerStatus(dealerId, { resale_cert_notes: notesDraft || null });
      setStatus(updated);
      setEditingNotes(false);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card title="Sales Tax" className="mt-4"><LoadingSpinner /></Card>;
  if (!status) return null;

  return (
    <Card title="Sales Tax" className="mt-4">
      {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-gray-900">
            {status.tax_exempt ? (
              <span className="text-green-700">✓ Tax Exempt</span>
            ) : (
              <span className="text-amber-700">Taxable — sales tax applied in active nexus states</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {status.tax_exempt
              ? 'No sales tax charged on orders shipping to states where Keder collects.'
              : 'No resale cert on file — orders shipping to active nexus states will be taxed.'}
          </p>
        </div>
        <button
          onClick={toggleExempt}
          disabled={saving}
          className={`px-3 py-1.5 rounded text-sm font-medium ${
            status.tax_exempt ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          } disabled:opacity-50`}
        >
          {status.tax_exempt ? 'Mark Taxable' : 'Mark Exempt'}
        </button>
      </div>

      <div className="border-t pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase text-gray-500">Resale Cert</span>
          {status.resale_cert_url ? (
            <a href={status.resale_cert_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
              View on file ↗
            </a>
          ) : (
            <span className="text-xs text-gray-400">None on file (lives in JotForm if exists)</span>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase text-gray-500">Notes</span>
            {!editingNotes && (
              <button onClick={() => setEditingNotes(true)} className="text-xs text-blue-600 hover:underline">Edit</button>
            )}
          </div>
          {editingNotes ? (
            <div className="mt-1 space-y-1">
              <textarea
                rows={2}
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
                placeholder="e.g. cert # 3-12345-9 (TX 01-339); home state OK"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => { setEditingNotes(false); setNotesDraft(status.resale_cert_notes || ''); }} className="text-xs text-gray-600">Cancel</button>
                <button onClick={saveNotes} disabled={saving} className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded">Save</button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700">{status.resale_cert_notes || <span className="text-gray-400">—</span>}</p>
          )}
        </div>
      </div>
    </Card>
  );
}


function InfoRow({
  label,
  value,
  field,
  editField,
  editValue,
  saving,
  onEdit,
  onSave,
  onCancel,
  onChange,
  placeholder,
}: {
  label: string;
  value: string | null | undefined;
  field: string;
  editField: string | null;
  editValue: string;
  saving: boolean;
  onEdit: (field: string, value: string) => void;
  onSave: (field: string) => void;
  onCancel: () => void;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const isEditing = editField === field;

  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      {isEditing ? (
        <div className="flex gap-1">
          <input
            type="text"
            value={editValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave(field);
              if (e.key === 'Escape') onCancel();
            }}
          />
          <button
            onClick={() => onSave(field)}
            disabled={saving}
            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? '...' : 'Save'}
          </button>
          <button onClick={onCancel} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">
            Cancel
          </button>
        </div>
      ) : (
        <p
          onClick={() => onEdit(field, value || '')}
          className="text-sm text-gray-900 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 py-0.5"
        >
          {value || <span className="text-gray-400 italic">{placeholder || 'Click to edit'}</span>}
        </p>
      )}
    </div>
  );
}
